import { useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { FormField } from "@/components/auth/FormField";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useChapters, useSubjects } from "@/hooks/useCurriculum";
import { useMyCramPlan } from "@/hooks/useCramPlan";
import { cn } from "@/lib/cn";
import type { CramStep } from "@/lib/api-types";

const CRAM_ACTIVITY_LABEL: Record<CramStep["activity"], string> = {
  summary: "Read summary",
  flashcards: "Flashcards",
  quiz: "Chapter quiz",
  mixed_test: "Mixed test",
};

/**
 * Student-facing cram plan (Phase 8) -- "exam tomorrow, help me plan tonight".
 * Same deterministic planner the parent board-exam hub uses, surfaced to the
 * student directly. Subject -> chapters -> hours, then an ordered
 * summary/flashcards/quiz/test sequence sized to the time available.
 */
export function CramPlanBuilder() {
  const me = useCurrentUser();
  const subjects = useSubjects(me.data?.class_id ?? undefined);
  const cramPlan = useMyCramPlan();

  const [subjectId, setSubjectId] = useState("");
  const [chapterIds, setChapterIds] = useState<string[]>([]);
  const [hours, setHours] = useState(2);
  const chapters = useChapters(subjectId || undefined);

  const onGenerate = async () => {
    if (chapterIds.length === 0) return;
    try {
      await cramPlan.mutateAsync({ chapter_ids: chapterIds, hours_available: hours });
    } catch {
      toast.error("Couldn't build a cram plan.");
    }
  };

  return (
    <Card className="p-4 sm:p-5">
      <p className="font-medium text-sm mb-3">Plan a cram session</p>
      <FormField id="cram_subject_student" label="Subject">
        <select
          id="cram_subject_student"
          value={subjectId}
          onChange={(e) => { setSubjectId(e.target.value); setChapterIds([]); }}
          className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm text-text focus-visible:outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <option value="">Pick a subject</option>
          {(subjects.data ?? []).map((s) => (
            <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>
          ))}
        </select>
      </FormField>

      {subjectId ? (
        <FormField id="cram_chapters_student" label="Chapters" className="mt-3" hint={`${chapterIds.length}/12 selected`}>
          <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto">
            {chapters.isLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              (chapters.data ?? []).map((c) => {
                const checked = chapterIds.includes(c.chapter_id);
                return (
                  <label
                    key={c.chapter_id}
                    className={cn(
                      "flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer text-sm transition-colors",
                      checked ? "border-brand bg-brand-soft" : "border-border hover:bg-surface-2",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setChapterIds((prev) =>
                          checked ? prev.filter((id) => id !== c.chapter_id) : [...prev, c.chapter_id].slice(0, 12),
                        )
                      }
                      className="h-4 w-4 accent-brand"
                    />
                    {c.chapter_name}
                  </label>
                );
              })
            )}
          </div>
        </FormField>
      ) : null}

      <FormField id="cram_hours_student" label="Hours you have" className="mt-3">
        <Input
          id="cram_hours_student"
          type="number"
          min={0.5}
          step={0.5}
          value={hours}
          onChange={(e) => setHours(Math.max(0.5, Number(e.target.value) || 0.5))}
        />
      </FormField>

      <Button className="mt-4" fullWidth loading={cramPlan.isPending} disabled={chapterIds.length === 0} onClick={onGenerate}>
        Build my plan
      </Button>

      {cramPlan.data ? (
        <div className="mt-4 border-t border-border pt-4">
          {cramPlan.data.dropped_chapter_ids.length > 0 ? (
            <p className="text-xs text-text-subtle mb-2">
              {cramPlan.data.dropped_chapter_ids.length} chapter(s) didn't fit in your time -- focus on the ones below first.
            </p>
          ) : null}
          <div className="flex flex-col gap-1.5">
            {cramPlan.data.steps.map((step, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate">
                  {step.chapter_name ?? "All selected chapters"}
                  <span className="text-text-subtle"> - {CRAM_ACTIVITY_LABEL[step.activity]}</span>
                </span>
                <span className="text-xs text-text-subtle flex-shrink-0">{step.minutes} min</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Card>
  );
}
